import { useState }    from "react";
import { User, FileText, BookOpen, Target, Lock } from "lucide-react";

import Avatar          from "../components/shared/Avatar";
import OverviewTab     from "../components/tabs/OverviewTab";
import DocumentsTab    from "../components/tabs/DocumentsTab";
import TrainingTab     from "../components/tabs/TrainingTab";
import GoalsTab        from "../components/tabs/GoalsTab";
import { useProfile }  from "../hooks/useProfile";

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
  { key: "overview",  label: "Overview",  icon: User     },
  { key: "documents", label: "Documents", icon: FileText  },
  { key: "training",  label: "Training",  icon: BookOpen  },
  { key: "goals",     label: "Goals",     icon: Target    },
];

/**
 * ProfileTest
 * The main profile page. Intentionally thin — all business logic lives in
 * hooks, all UI in tab components, all content in data files.
 *
 * To toggle the onboarding gate:
 *   Edit BYPASS_LOCK in profile/utils/profileHelpers.js
 */
export default function ProfileTest() {
  const [activeTab, setActiveTab] = useState("overview");
  const { user, setUser, docsLocked, handleDocSigned } = useProfile();

  return (
    <div className="page">

      {/* Page header */}
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Avatar name={user?.full_name} email={user?.email} size={48} />
          <div>
            <div className="page-title">{user?.full_name ?? "My Profile"}</div>
            <div className="page-subtitle">{user?.title ?? "Sales Representative"} · {user?.email}</div>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="settings-tabs-bar">
        {TABS.map(({ key, label, icon: Icon }) => {
          const isLocked = (key === "training" || key === "goals") && docsLocked;
          return (
            <button
              key={key}
              className={`settings-top-tab ${activeTab === key ? "active" : ""}`}
              onClick={() => setActiveTab(key)}
              title={isLocked ? "Complete onboarding to unlock" : undefined}
            >
              <Icon size={14} />{label}
              {isLocked && <Lock size={11} style={{ marginLeft: 2, color: "#94a3b8" }} />}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="settings-tab-content" style={{ maxWidth: 780 }}>
        {activeTab === "overview"  && (
          <OverviewTab
            user={user}
            onEditSave={updates => setUser(u => ({ ...u, ...updates }))}
          />
        )}
        {activeTab === "documents" && (
          <DocumentsTab user={user} onDocSigned={handleDocSigned} />
        )}
        {activeTab === "training"  && <TrainingTab locked={docsLocked} />}
        {activeTab === "goals"     && <GoalsTab    locked={docsLocked} />}
      </div>

    </div>
  );
}